import { Users } from "lucide-react"
import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Progress } from "@/components/ui/progress"
import { Skeleton } from "@/components/ui/skeleton"
import { CreatorStudent } from "@/hooks/useCreatorStudents"
import { StudentPagination } from "./StudentPagination"
import { useEffect } from "react"

interface StudentMobileListProps {
  students: CreatorStudent[];
  isLoading: boolean;
  openStudentDetail: (id: string) => void;
  currentPage: number;
  onPageChange: (page: number) => void;
  pageSize?: number;
  totalStudents: number;
}

export function StudentMobileList({
  students,
  isLoading,
  openStudentDetail,
  currentPage,
  onPageChange,
  pageSize = 10,
  totalStudents
}: StudentMobileListProps) {
  useEffect(() => {
    console.log("StudentMobileList - Received students:", students);
    if (students && students.length > 0) {
      console.log("StudentMobileList - Sample student:", students[0]);
      console.log("StudentMobileList - Total students received:", students.length);
    }
  }, [students]);

  if (isLoading) {
    console.log("StudentMobileList - Loading state active");
    return (
      <div className="space-y-4">
        {Array(3).fill(0).map((_, i) => (
          <Skeleton key={i} className="h-40 w-full" />
        ))}
      </div>
    );
  }

  if (!students || students.length === 0) {
    console.log("StudentMobileList - No students found");
    return (
      <div className="text-center py-8 border rounded-md bg-muted/5">
        <Users className="mx-auto h-8 w-8 text-muted-foreground" />
        <p className="mt-2 text-muted-foreground">No students enrolled yet</p>
        <p className="text-sm text-muted-foreground">Students will appear here once they enroll in your courses</p>
      </div>
    );
  }

  const totalPages = Math.ceil(totalStudents / pageSize);
  
  console.log("StudentMobileList - Render state:", {
    displayedStudents: students.length,
    totalStudents,
    currentPage,
    totalPages
  });

  return (
    <div className="space-y-4">
      {students.map((student) => {
        console.log(`Rendering mobile student card: ${student.student_id}, ${student.student_name}`);
        return (
          <Card key={`${student.student_id}-${student.course_id}`} className="overflow-hidden">
            <div className="flex items-center border-b p-4">
              <div className="flex-1">
                <h3 className="font-medium">{student.student_name || "N/A"}</h3>
                <p className="text-sm text-muted-foreground">{student.student_email}</p>
              </div>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => openStudentDetail(student.student_id)}
              >
                View
              </Button>
            </div>
            <div className="p-4 grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">Course</p>
                <p className="font-medium truncate">{student.course_title}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Revenue</p>
                <p className="font-medium">${student.total_revenue.toFixed(2)}</p>
              </div>
              <div className="col-span-2">
                <div className="flex justify-between text-sm mb-1">
                  <span>Progress</span>
                  <span>{student.progress_percent}%</span>
                </div>
                <Progress value={student.progress_percent} className="h-2" />
              </div>
              <div className="col-span-2">
                <p className="text-sm text-muted-foreground">Last Active</p>
                <p className="font-medium">
                  {student.last_active ? new Date(student.last_active).toLocaleDateString() : "Never"}
                </p>
              </div>
            </div>
          </Card>
        );
      })}
      
      <StudentPagination
        page={currentPage}
        pageSize={pageSize}
        totalPages={totalPages}
        totalItems={totalStudents}
        onPageChange={onPageChange}
      />
    </div>
  );
}
